import { controlPanelShell as cps } from "./foundry-parts.generated";
/**
 * @fileoverview GettingStartedCard — horizontal interactive card in the
 * Control Panel home page's Getting Started row. Leading colored 25×25
 * illustration + h6 title + arrow indicator on the right; muted
 * description below.
 *
 * Foundry mapping: `control-panel-shell__card__cti876 bp6-card
 * bp6-elevation-0 bp6-interactive` + `__card-header` + `__title-container`.
 */

import { H6, Icon } from "@blueprintjs/core";
import type { ReactElement, ReactNode } from "react";

interface GettingStartedCardProps {
  /** 25×25 illustration node (typically an inline SVG/img). */
  illustration: ReactNode;
  title: string;
  description: ReactNode;
  href?: string;
  onClick?: () => void;
  className?: string;
}

export function GettingStartedCard({
  illustration,
  title,
  description,
  href,
  onClick,
  className,
}: GettingStartedCardProps): ReactElement {
  /*
    THE RIGHT CARD. My first attempt used `control-panel-app__card__1o33bj1`,
    which is the RESOURCES tile — that module also exports `violet`,
    `turquoise`, `gray` and `resources-container`, and its card is a centred
    block, which is exactly how ours then rendered. The capture puts the
    getting-started rows in `control-panel-shell__card__cti876` inside
    `__anchor__cti876`, a different module with a horizontal header.

    Two parts sharing a name across modules is the same trap the launcher hit;
    the difference here is that both resolve, so the wrong one styles the
    element instead of leaving it bare. Reading the capture at the point of use
    is the only thing that separates them.
  */
  const classes = [
    "bp6-card bp6-elevation-0 bp6-interactive",
    cps["card"],
    "bp-getting-started-card",
    className,
  ].filter(Boolean).join(" ");

  const body = (
    <>
      <div className={`${cps["card-header"]} bp-getting-started-card-header`}>
        <div className={`${cps["title-container"]} bp-getting-started-card-title-block`}>
          <span>{illustration}</span>
          <H6 className="bp-getting-started-card-title">{title}</H6>
        </div>
        <Icon icon="arrow-right" intent="primary" />
      </div>
      <div className="bp6-text-muted">{description}</div>
    </>
  );

  if (href !== undefined) {
    return (
      /*
        ANCHOR AROUND CARD, two elements. I had merged them onto one `<a>`, and
        the capture nests them: `a.__anchor__` wraps `div.__card__.bp6-card`.
        That one collapse accounted for twenty structural findings — every card
        and every child of every card sat one level shallower than its
        counterpart, so none of them matched.
      */
      <a data-testid="getting-started-card" href={href} className={cps["anchor"]}>
        <div className={classes}>{body}</div>
      </a>
    );
  }
  return (
    <button type="button" onClick={onClick} className={`${cps["anchor"]} ${classes}`}>
      {body}
    </button>
  );
}
